import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import loginImage from "../assets/login.webp";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
import { useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { HttpError } from "@/http-error";
import { LockIcon, ArrowLeftIcon } from "lucide-react";
import { setInitialPasswordMutation } from "@/client/@tanstack/react-query.gen";
import { useNavigate } from "@tanstack/react-router";
import { AuthFormFooter } from "@/components/auth-form-footer";

interface NewUserSetupFormProps {
  token: string;
  className?: string;
}

export function NewUserSetupForm({
  token,
  className,
  ...props
}: NewUserSetupFormProps & React.ComponentProps<"div">) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const formSchema = useMemo(
    () =>
      z
        .object({
          password: z
            .string()
            .min(8, { message: t("auth.errors.passwordMinLength") })
            .max(50, { message: t("auth.errors.passwordMaxLength") }),
          confirmPassword: z
            .string()
            .min(8, { message: t("auth.errors.passwordMinLength") })
            .max(50, { message: t("auth.errors.passwordMaxLength") }),
        })
        .refine((data) => data.password === data.confirmPassword, {
          message: t("auth.errors.passwordsDoNotMatch"),
          path: ["confirmPassword"],
        }),
    [t],
  );

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: "onBlur",
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const mutation = useMutation({
    ...setInitialPasswordMutation(),
    onError: (error) => {
      if (error instanceof HttpError && error.status === 400) {
        toast.error(t("auth.errors.invalidSetupToken"), {
          icon: <LockIcon />,
          description: t("auth.errors.invalidSetupTokenDesc"),
          richColors: true,
        });
      } else {
        toast.error(t("auth.errors.setupPasswordFailed"), {
          description: t("auth.errors.setupPasswordFailedDesc"),
          richColors: true,
        });
      }
    },
    onSuccess: () => {
      toast.success(t("auth.newUserSetup.success"), {
        description: t("auth.newUserSetup.successDesc"),
        richColors: true,
      });
      navigate({ to: "/login", search: { redirect: undefined } });
    },
  });

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card className="overflow-hidden p-0">
        <CardContent className="grid p-0 md:grid-cols-2">
          <Form {...form}>
            <form
              className="p-6 md:p-8"
              onSubmit={form.handleSubmit(({ password }) =>
                mutation.mutate({
                  body: { nonce: token, newPassword: password },
                })
              )}
            >
              <div className="flex flex-col gap-6">
                <div className="flex flex-col items-center text-center">
                  <h1 className="text-2xl font-bold">
                    {t("auth.newUserSetup.title")}
                  </h1>
                  <p className="text-muted-foreground text-balance">
                    {t("auth.newUserSetup.subtitle")}
                  </p>
                </div>
                <div className="grid gap-3">
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t("auth.newUserSetup.newPassword")}
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="password"
                            required
                            autoComplete="new-password"
                            tabIndex={1}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage></FormMessage>
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid gap-3">
                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t("auth.newUserSetup.confirmPassword")}
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="password"
                            required
                            autoComplete="new-password"
                            tabIndex={2}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage></FormMessage>
                      </FormItem>
                    )}
                  />
                </div>
                <Button type="submit" className="w-full" tabIndex={3}>
                  {t("auth.newUserSetup.submitButton")}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() =>
                    navigate({ to: "/login", search: { redirect: undefined } })
                  }
                  tabIndex={4}
                >
                  <ArrowLeftIcon className="mr-2 h-4 w-4" />
                  {t("auth.newUserSetup.backToLogin")}
                </Button>
                <div className="flex justify-center pt-2">
                  <AuthFormFooter />
                </div>
              </div>
            </form>
          </Form>
          <div className="bg-muted relative hidden md:block">
            <img
              src={loginImage}
              alt="Image"
              className="absolute inset-0 h-full w-full object-cover dark:brightness-[0.2] dark:grayscale"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
